import React, { useMemo, useState } from 'react';
import {
  GetNotificationPreferencesAndLevelQuery,
  NotificationPrefDetails,
} from '../../../../../../../__generated__/loma';
import Subscribed from './components/Subscribed';
import { CprefMarketingState } from './types';
import { useSubscribeNotificationPreferenceMutate } from '../../hooks/useSubscribeNotificationPreferenceMutate';
import { Channels } from '../../types';
import MarketingSubscribed from './components/MarketingSubscribed';
import MarketingUnsubscribed from './components/MarketingUnsubscribed';
import { useUnsubscribeNotificationPreferenceMutate } from '../../hooks/useUnsubscribeNotificationPreferenceMutate';
import { ERROR_TYPES } from '../..';
import {
  NarvarEventComponents,
  NarvarEventTypes,
  useAnalyticsPost,
} from '../../../../../hooks/Analytics';
import { usePageContext } from '../../../../../hooks/PageContext/usePageContext';
import CprefMarketingSubscribed from './components/CprefMarketingSubscribed';

interface CprefMarketingProps {
  featureName?: string;
  isLandscape: boolean;
  contact?: string;
  cprefOptedIn: boolean;
  cprefSubscribed: boolean;
  narvarProfileUrl: string;
  getNotificationPreferencesAndLevelData?: GetNotificationPreferencesAndLevelQuery;
  onCprefExit: () => void;
}

export const CprefMarketing = ({
  featureName,
  isLandscape,
  contact,
  cprefOptedIn,
  cprefSubscribed,
  getNotificationPreferencesAndLevelData: preferencesData,
  narvarProfileUrl,
  onCprefExit,
}: CprefMarketingProps) => {
  const { getNotificationPreferencesAndLevel } = preferencesData ?? {};
  const { notificationPrefDetails } = getNotificationPreferencesAndLevel ?? {};

  const networkSubscribed = notificationPrefDetails
    ? notificationPrefDetails?.length > 0
    : false;

  const marketingSubscribed = networkSubscribed && !contact;

  const [cprefState, setCprefState] = useState<CprefMarketingState>(() => {
    if (marketingSubscribed && cprefSubscribed) {
      return 'cpref-marketing-subscribed';
    }

    if (cprefSubscribed || contact) {
      return 'subscribed';
    }

    return 'marketing-subscribed';
  });

  const [showContactInput, setShowContactInput] = useState(false);
  const [validationError, setValidationError] = useState(false);
  const [marketingOptinSuccess, setMarketingOptinSuccess] = useState(false);

  const {
    state: { orderNumbers, trackingNumbers },
  } = usePageContext();

  const orderId = orderNumbers?.[0];

  const logEvent = useAnalyticsPost();

  const baseLogInfo = useMemo(
    () => ({
      eventComponent: NarvarEventComponents.NTH_PACK_NOTIFICATION_CENTER,
      eventFeature: NarvarEventComponents.NTH_PACK_NOTIFICATION_CENTER,
      feature: featureName,
      orderNumber: orderId,
      pageComponent: NarvarEventComponents.NTH_PACK_NOTIFICATION_CENTER,
      pageOrderNumber: orderId,
    }),
    [featureName, orderId],
  );

  const { onSubscribeMutate, loading: subscribing } =
    useSubscribeNotificationPreferenceMutate({
      data: preferencesData,
      options: {
        onCompleted: () => {
          setCprefState('marketing-subscribed');
          setMarketingOptinSuccess(true);
        },
        onError(error) {
          if (error && error.message.includes(ERROR_TYPES.VALIDATION_ERROR)) {
            setValidationError(true);
            logEvent({
              ...baseLogInfo,
              eventNotificationSmsValidationError: `${
                error.message
              }; cpref; page trackingNumber: ${
                trackingNumbers?.toString() || ''
              }`,
              eventType: NarvarEventTypes.ERROR,
            });
          }
        },
      },
    });

  const { onUnsubscribeMutate, loading: unsubscribing } =
    useUnsubscribeNotificationPreferenceMutate({
      data: preferencesData,
      options: {
        onCompleted: () => {
          setCprefState('marketing-unsubscribed');
        },
      },
    });

  const handleMarketingOptinSubmit = (contact: string) => {
    const notificationPrefDetails: NotificationPrefDetails[] = [
      { channel: 'sms', contact },
    ];

    onSubscribeMutate(Channels.SMS, notificationPrefDetails, false, true);
  };

  const handleTelephoneInputChange = () => {
    setValidationError(false);
  };

  const handleMarketingOptout = () => {
    onUnsubscribeMutate(Channels.SMS);
  };

  const handleSignUpAgainClick = () => {
    if (!cprefSubscribed && !cprefOptedIn) {
      onCprefExit();
      return;
    }

    setCprefState('subscribed');
    setShowContactInput(true);
  };

  return (
    <>
      {cprefState === 'subscribed' && (
        <Subscribed
          contact={contact}
          featureName={featureName}
          isLandscape={isLandscape}
          validationError={validationError}
          cprefOptedIn={cprefOptedIn}
          cprefSubscribed={cprefSubscribed}
          marketingSubscribed={marketingSubscribed}
          networkSubscribed={networkSubscribed}
          showContactInput={showContactInput}
          subscribing={subscribing}
          unsubscribing={unsubscribing}
          narvarProfileUrl={narvarProfileUrl}
          onTelephoneInputChange={handleTelephoneInputChange}
          onMarketingOptinSubmit={handleMarketingOptinSubmit}
          onMarketingOptout={handleMarketingOptout}
        />
      )}
      {cprefState === 'marketing-subscribed' && (
        <MarketingSubscribed
          featureName={featureName}
          isLandscape={isLandscape}
          marketingOptinSuccess={marketingOptinSuccess}
          unsubscribing={unsubscribing}
          onChangeNumberClick={handleSignUpAgainClick}
          onUnsubscribeClick={handleMarketingOptout}
        />
      )}
      {cprefState === 'marketing-unsubscribed' && (
        <MarketingUnsubscribed
          featureName={featureName}
          isLandscape={isLandscape}
          onSignUpAgainClick={handleSignUpAgainClick}
        />
      )}
      {cprefState === 'cpref-marketing-subscribed' && (
        <CprefMarketingSubscribed
          featureName={featureName}
          isLandscape={isLandscape}
          narvarProfileUrl={narvarProfileUrl}
          onSignUpAgainClick={handleSignUpAgainClick}
        />
      )}
    </>
  );
};
